import React, { Component, Fragment } from 'react';

import StudentList from './studentList';
import { ConnectedEditChildModal } from '../../../styleguide/ChildModal';
import DeleteChildModal from './deleteChildModal';
import BulkEditChildModal from './bulkEditChildModal';
import BulkDeleteChildModal from './bulkDeleteChildModal';
import SpinningWheel from '../../../components/SpinningWheel'

import API from './api';
import './my-students.less';
import trackEvent from "../eventTracking";

class MyStudentsPage extends Component {
  constructor(props) {
    super(props);

    // Show error message if user tried to navigate to "/my/students/add/" when they have no available seats
    let addStudentRedirectError = false;
    if (this.props.location.state && this.props.history.action == "REPLACE") {
      addStudentRedirectError = this.props.location.state.addStudentRedirectError || false;
    }

    this.state = {
      loading: false,
      yourX: props.yourX,
      addStudentRedirectError: addStudentRedirectError,

      currentPlayer: null,  // For individual edit/delete
      selectedChildren: {},  // For bulk edits/delete

      editChildModalOpened: false,
      deleteChildModalOpened: false,
      bulkEditChildModalOpened: false,
      bulkDeleteChildModalOpened: false,

      editChildErrorMessage: '',

      defaultBulkEditGrade: 'kindergarten',
      defaultBulkEditLockedGrade: false,
      defaultBulkEditHiddenGrade: false
    }
  }

  toggleSelected = (id) => {
    let indx = Object.keys(this.state.selectedChildren).indexOf(id);

    if (indx < 0) {  // Add
      this.setState({
        selectedChildren: {
          ...this.state.selectedChildren,
          [id]: this.props.players[id]
        }
      })
    }
    else {  // Remove
      let newSelectedChildren = {};
      Object.keys(this.state.selectedChildren).forEach(playerId => {
        if (playerId !== id) {
          newSelectedChildren[playerId] = this.state.selectedChildren[playerId];
        }
      });
      this.setState({
        selectedChildren: newSelectedChildren
      })
    }
  };

  toggleSelectHeader = () => {
    if (Object.keys(this.state.selectedChildren).length < Object.keys(this.props.players).length) {
      this.setState({
        selectedChildren: {...this.props.players}
      })
    } else {
      this.setState({
        selectedChildren: {}
      })
    }
  };

  updateCountsAA = (aa, value) => {
    if (value in aa) {
      aa[value] += 1;
    } else {
      aa[value] = 1;
    }
  };

  getKeyWithMaxCount = (aa) => {
    let maxKey = null;
    let maxCount = -1;

    Object.keys(aa).forEach(key => {
      if (aa[key] > maxCount) {
        maxKey = key;
        maxCount = aa[key];
      }
    });

    return maxKey;
  };

  getDefaultBulkEditSettings = () => {
    // NOTE: Want to keep the default settings updated, so the modal doesn't have to do all this work on-open
    let gradeCounts = {};
    let lockedGradeCounts = {};
    let hiddenGradeCounts = {};

    // 1. Count
    Object.keys(this.state.selectedChildren).forEach(id => {
      let values = this.props.players[id];
      this.updateCountsAA(gradeCounts, values.grade);
      this.updateCountsAA(lockedGradeCounts, values.lockedGrade);
      this.updateCountsAA(hiddenGradeCounts, values.hiddenGrade);
    });

    // 2. Ones with max count
    return {
      grade: this.getKeyWithMaxCount(gradeCounts),
      lockedGrade: this.getKeyWithMaxCount(lockedGradeCounts) === 'true',  //Key is in str
      hiddenGrade: this.getKeyWithMaxCount(hiddenGradeCounts) === 'true'
    };
  };

  getPlayerWithAPIResponse = (player, resp) => {
    if (resp.avatar && resp.avatar.internal_name) {
      resp.avatar.internalName = resp.avatar.internal_name;
    }

    return {
      ...player,
      childId: resp.PlayerId,
      name: resp.PlayerName,
      grade: resp.PlayerGrade,
      avatar: resp.avatar,
      lockedGrade: resp.lockedGrade || false,
      hiddenGrade: resp.hiddenGrade || false
    };
  };

  ////////////////////////////////////////////
  // Edit Child Modal
  ////////////////////////////////////////////

  openEditChildModal = (playerId) => {
    trackEvent('edit', 'started', false);

    this.setState({
      editChildModalOpened: true,
      currentPlayer: this.props.players[playerId]
    });
  };

  closeEditChildModal = () => {
    this.setState({
      editChildModalOpened: false,
      currentPlayer: null
    })
  };

  editChildCallback = (child) => {
    trackEvent('edit', 'completed', false);

    this.setState({
      editChildModalOpened: false
    });
    this.props.updatePlayers({
      ...this.props.players,
      [child.id]: {
        ...this.props.players[child.id],
        childId: child.id,
        name: child.name,
        grade: child.grade,
        avatar: {internalName: child.avatar},
        lockedGrade: child.isGradeLocked,
        hiddenGrade: child.isGradeHidden
      }
    });
  };

  toggleLockHideGrade = (playerId, lockOrHide='lock') => {
    // 1. Get the player
    const player = this.props.players[playerId];

    // 2. Modal off. Loading on.
    this.setState({
      loading: true
    });

    // 3. Tell the server
    API.editChild(player.childId, {
      name: player.name,
      avatar: player.avatar.internalName,
      grade: player.grade,
      lockedGrade: (lockOrHide==='lock' ? !player.lockedGrade : player.lockedGrade),
      hiddenGrade: (lockOrHide==='hide' ? !player.hiddenGrade : player.hiddenGrade)
    })
      .then((resp) => {
        const updatedPlayer = this.getPlayerWithAPIResponse(player, resp)

        this.props.updatePlayers({
          ...this.props.players,
          [player.childId]: updatedPlayer
        });
      })
      .finally(() => {
        this.setState({loading: false});
      })
  };

  ////////////////////////////////////////////
  // Delete Child Modal
  ////////////////////////////////////////////

  openDeleteChildModal = (playerId) => {
    this.setState({
      deleteChildModalOpened: true,
      currentPlayer: this.props.players[playerId]
    });
  };

  closeDeleteChildModal = () => {
    this.setState({
      deleteChildModalOpened: false,
      currentPlayer: null
    })
  };

  deleteChildCallback = () => {
    trackEvent('delete', 'completed', false);

    const playerId = this.state.currentPlayer.childId;

    // 1. Modal off. Loading on.
    this.setState({
      deleteChildModalOpened: false,
      loading: true
    });

    // 2. Tell server
    API.removeChild(playerId)
      .then(() => {
        let newPlayers = {};
        Object.keys(this.props.players).forEach(id => {
          if (id !== String(playerId)) {  // id is str, playerId is number
            newPlayers[id] = this.props.players[id];
          }
        });

        let newSelectedChildren = {};
        Object.keys(this.state.selectedChildren).forEach(id => {
          if (id !== String(playerId)) {  // id is str, playerId is number
            newSelectedChildren[id] = this.props.players[id];
          }
        });

        this.setState({
          selectedChildren: newSelectedChildren
        });
        this.props.updatePlayers({
          ...newPlayers
        });
      })
      .finally(() => {
        this.setState({
          loading: false
        });
      });
  };

  ////////////////////////////////////////////
  // Bulk Edit Child Modal
  ////////////////////////////////////////////

  openBulkEditChildModal = () => {
    trackEvent('edit', 'started', true);

    const settings = this.getDefaultBulkEditSettings();  //Re-calculate default values right before opening modal

    this.setState({
      ...this.state,
      defaultBulkEditGrade: settings.grade,
      defaultBulkEditLockedGrade: settings.lockedGrade,
      defaultBulkEditHiddenGrade: settings.hiddenGrade
    }, () => {
      this.setState ({
        bulkEditChildModalOpened: true
      })
    })
  };

  closeBulkEditChildModal = () => {
    this.setState({
      bulkEditChildModalOpened: false,
    });
  };

  editBulkChildCallback = (fields) => {
    /*
    fields is the BulkEditChildModal instance's state
    that is a map with following keys:
      grade, lockedGrade, hiddenGrade,
     */

    const ids = Object.keys(this.state.selectedChildren);

    // 1. Modal off. Loading on.
    this.setState({
      bulkEditChildModalOpened: false,
      loading: true
    });

    // 2. Tell the server about the update
    API.bulkEditSettings(ids, {
      grade: fields.grade,
      lockedGrade: fields.lockedGrade || false,
      hiddenGrade: fields.hiddenGrade || false
    })
      .then((resp) => {
        const newPlayersData = resp.result;

        let newPlayers = {};
        newPlayersData.forEach((player) => {
          const id = player.PlayerId;
          newPlayers[id] = {
            ...this.props.players[id],
            grade: player.PlayerGrade,
            lockedGrade: player.lockedGrade,
            hiddenGrade: player.hiddenGrade
          }
        });

        this.setState({
          loading: false
        });
        this.props.updatePlayers({
          ...this.props.players,
          ...newPlayers
        });
      })
      .catch(() => {
        this.setState({
          loading: false
        })
      });
  };

  ////////////////////////////////////////////
  // Bulk Delete Child Modal
  ////////////////////////////////////////////

  openBulkDeleteChildModal = () => {
    trackEvent('delete', 'started', true);

    this.setState ({
      bulkDeleteChildModalOpened: true
    })
  };

  closeBulkDeleteChildModal = () => {
    this.setState({
      bulkDeleteChildModalOpened: false,
    });
  };

  deleteBulkChildCallback = () => {
    trackEvent('delete', 'completed', true);

    const removedPlayers = this.state.selectedChildren;

    // 1. Modal off. Loading on.
    this.setState({
      bulkDeleteChildModalOpened: false,
      loading: true
    });

    // 2. Tell server
    API.bulkRemove(Object.keys(removedPlayers))
      .then(() => {
        let newPlayers = {};
        Object.keys(this.props.players).forEach(id => {
          if (!(id in removedPlayers)) {
            newPlayers[id] = this.props.players[id];
          }
        });
        this.props.updatePlayers(newPlayers);
      })
      .finally(() => {
        this.setState({
          selectedChildren: {},
          loading: false
        });
      })
  };

  ////////////////////////////////////////////
  // Print student codes:: NEED TO TEST
  ////////////////////////////////////////////

  onPrintCodesClick = () => {
    trackEvent('print codes', 'started and completed', true);

    let url = "/my/students/download/student_codes/?ids=";
    url += Object.keys(this.state.selectedChildren).join(',');
    window.open(url, "_blank");
  };

  ////////////////////////////////////////////

  render() {
    const { currentPlayer } = this.state;
    const yourX = this.state.yourX.plural;
    const title = 'My '+ yourX.charAt(0).toUpperCase() + yourX.slice(1);

    return (
      <div className="my-students">
        <h1>{title}</h1>
        <StudentList
          key='studentList'
          yourX={this.state.yourX}
          players={this.props.players}
          maxPlayers={this.props.maxPlayers}
          lengthOfPlayers={Object.keys(this.props.players).length}
          toggleLockHideGrade={this.toggleLockHideGrade}
          openEditChildModal={this.openEditChildModal}
          openDeleteChildModal={this.openDeleteChildModal}
          openBulkEditChildModal={this.openBulkEditChildModal}
          openBulkDeleteChildModal={this.openBulkDeleteChildModal}
          selectedChildren={this.state.selectedChildren}
          lengthOfSelected={Object.keys(this.state.selectedChildren).length}
          toggleSelected={this.toggleSelected}
          toggleSelectHeader={this.toggleSelectHeader}
          onPrintCodesClick={this.onPrintCodesClick}
          addStudentRedirectError={this.state.addStudentRedirectError}
        />
        <ConnectedEditChildModal
          open={this.state.editChildModalOpened}
          child={currentPlayer != null ? {
            id: currentPlayer.childId,
            name: currentPlayer.name,
            grade: currentPlayer.grade,
            avatar: currentPlayer.avatar.internalName,
            isGradeLocked: currentPlayer.lockedGrade,
            isGradeHidden: currentPlayer.hiddenGrade
          } : null}
          yourX={this.state.yourX}
          onClose={this.closeEditChildModal}
          onSuccess={this.editChildCallback}
        />
        <DeleteChildModal
          key='deleteChildModal'
          open={this.state.deleteChildModalOpened}
          currentPlayer={this.state.currentPlayer}
          closeDeleteChildModal={this.closeDeleteChildModal}
          deleteChildCallback={this.deleteChildCallback}
        />
        <BulkEditChildModal
          key='bulkEditChildModal'
          yourX={this.state.yourX}
          open={this.state.bulkEditChildModalOpened}
          closeBulkEditChildModal={this.closeBulkEditChildModal}
          editBulkChildCallback={this.editBulkChildCallback}
          lengthOfSelected={Object.keys(this.state.selectedChildren).length}
          defaultBulkEditGrade={this.state.defaultBulkEditGrade}
          defaultBulkEditLockedGrade={this.state.defaultBulkEditLockedGrade}
          defaultBulkEditHiddenGrade={this.state.defaultBulkEditHiddenGrade}
        />
        <BulkDeleteChildModal
          key='bulkDeleteChildModal'
          yourX={this.state.yourX}
          open={this.state.bulkDeleteChildModalOpened}
          closeBulkDeleteChildModal={this.closeBulkDeleteChildModal}
          deleteBulkChildCallback={this.deleteBulkChildCallback}
          lengthOfSelected={Object.keys(this.state.selectedChildren).length}
        />
        {(this.state.loading || this.props.loading) && <SpinningWheel fullScreen={true} />}
      </div>
    );
  }
}

export default MyStudentsPage;
